// GL16 GPC 배송비 분담금 단가 관리 — React Query 훅.
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { QueryClient, UseMutationResult, UseQueryResult } from '@tanstack/react-query'
import { getGpcRateHistories, getGpcRateMatrix, saveGpcRatePrice } from './apis'
import type { GpcRateHistoryFilter, GpcRateHistoryRes, GpcRateMatrixFilter, GpcRateMatrixRes, GpcRateSaveReq, GpcRateSaveRes } from './types'
import { GPC_RATE_HISTORY_MAX_SIZE, GPC_RATE_MATRIX_MAX_SIZE } from './types'

/**
 * 메뉴코드는 소문자 'gl16' 이다. 탭을 닫을 때 `MenuOnSessionWrap.remove` 가 queryKey 요소를 훑어
 * 메뉴코드가 든 캐시만 지우는데, 그 코드가 `MENU_CD.toLowerCase()` 로 만들어진다(useAuthQueries.ts).
 * 대문자로 넣으면 `.includes` 가 안 걸려 캐시가 남는다(GL15 선례).
 */
export const gpcRateQueryKeys = {
  all: ['gl16', 'GPC_RATE'] as const,
  matrix: (filter: GpcRateMatrixFilter | null, page: number, size: number) =>
    [...gpcRateQueryKeys.all, 'matrix', filter, page, size] as const,
  histories: (filter: GpcRateHistoryFilter, page: number, size: number) =>
    [...gpcRateQueryKeys.all, 'histories', filter, page, size] as const,
}

/**
 * 매트릭스 조회 — 서버 페이지네이션(page 는 화면 페이저 1부터, BE 로는 0부터). 조회 조건이 없으면 호출하지 않는다.
 * queryKey 에는 조회 조건과 페이지만 담는다 — 같은 조건 재조회는 `refreshGpcRateMatrix` 로.
 */
export const useGpcRateMatrixQuery = (
  filter: GpcRateMatrixFilter | null,
  page: number,
  size: number,
): UseQueryResult<GpcRateMatrixRes, Error> =>
  useQuery({
    queryKey: gpcRateQueryKeys.matrix(filter, page, size),
    queryFn: async () => {
      const res = await getGpcRateMatrix({
        ...(filter as GpcRateMatrixFilter),
        page: Math.max(0, page - 1),
        size: Math.min(size, GPC_RATE_MATRIX_MAX_SIZE),
      })
      return res.data
    },
    enabled: !!filter?.basisDate,
    // 페이지 전환 중 이전 페이지 유지 — 표가 비었다 차는 깜빡임 방지(GL15 선례)
    placeholderData: keepPreviousData,
  })

/** 셀 단건 저장 — 성공하면 매트릭스를 무효화해 현재 페이지를 다시 받는다. */
export const useSaveGpcRatePriceMutation = (): UseMutationResult<ApiResponseV2<GpcRateSaveRes>, Error, GpcRateSaveReq> => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: saveGpcRatePrice,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: gpcRateQueryKeys.all }),
  })
}

/** 같은 조건 재조회 — [조회] 재클릭, 저장 충돌(409) 뒤 최신화. */
export const refreshGpcRateMatrix = (queryClient: QueryClient) =>
  queryClient.invalidateQueries({ queryKey: gpcRateQueryKeys.all })

/**
 * 변경 이력 — 서버 페이지네이션(page 는 화면 페이저 1부터, BE 로는 0부터, size 최대 100). enabled 가 false 면(이력 모달을 연 적 없음) 호출하지 않는다.
 * 저장 성공 뒤 `gpcRateQueryKeys.all` 무효화에 함께 걸려 다음에 열 때 최신이다.
 */
export const useGpcRateHistoriesQuery = (
  filter: GpcRateHistoryFilter,
  page: number,
  size: number,
  enabled: boolean,
): UseQueryResult<GpcRateHistoryRes, Error> =>
  useQuery({
    queryKey: gpcRateQueryKeys.histories(filter, page, size),
    queryFn: async () => {
      const res = await getGpcRateHistories({ ...filter, page: Math.max(0, page - 1), size: Math.min(size, GPC_RATE_HISTORY_MAX_SIZE) })
      return res.data
    },
    enabled,
    placeholderData: keepPreviousData,
  })

/** 이력 다시 받기 — [변경 이력]을 다시 열 때(모달이 닫혀 있는 동안 다른 사용자가 저장했을 수 있다). */
export const refreshGpcRateHistories = (queryClient: QueryClient) =>
  queryClient.invalidateQueries({ queryKey: [...gpcRateQueryKeys.all, 'histories'] })
